import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { DataTable } from '@/components/composites/DataTable';
import { ScreenshotImage } from '@/components/composites/ScreenshotImage';
import {
  ScreenshotLightbox,
  type ScreenshotLightboxEntry,
} from '@/components/composites/ScreenshotLightbox';
import { useAppContext } from '@/context/AppContext';
import { useActivityLogEvents } from '@/hooks/useActivityLogEvents';
import { formatDateTimeInTz } from '@/lib/dates';
import type { ActivityLogEvent, ActivityLogGroupBy } from '@/types/reports';
import styles from './ActivityLogEventsPage.module.css';

type ColumnKey = 'thumbnail' | 'timestamp' | 'employee' | 'event';

// Task 177 — alt-text rule. Mechanical, neutral, names what the
// artifact is. Aligns with the "professional's assistant, not
// surveillance" framing rule and SECURITY.md "Possibly sensitive"
// treatment of window titles (the alt text exposes the same string the
// visible Event-row already shows; it does not leak anything new).
function altTextForEvent(ev: ActivityLogEvent): string {
  const app = ev.app.trim();
  const title = ev.title.trim();
  if (app && title) return `Screenshot of ${app} — ${title}`;
  if (app) return `Screenshot of ${app}`;
  if (title) return `Screenshot of ${title}`;
  return 'Screenshot';
}

/**
 * Activity log event-list page — task 169 drill-down.
 *
 * Reached by clicking a row on /reports/activity-log. Shows every event
 * behind that row in chronological order (newest first), with timestamp
 * (local org tz), employee, and a varying-content "Event" column (site
 * → url → "—") since the constant `(app, title)` lives in the page
 * header.
 */
export function ActivityLogEventsPage() {
  const [searchParams] = useSearchParams();
  const { mockupMode, dateRange } = useAppContext();

  const periodQs = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return null;
    const params = new URLSearchParams();
    params.set('period', 'custom');
    params.set('startDate', dateRange.start);
    params.set('endDate', dateRange.end);
    return params.toString();
  }, [dateRange.start, dateRange.end]);

  const app = searchParams.get('app') ?? '';
  const title = searchParams.get('title') ?? '';
  const clientId = searchParams.get('clientId') ?? undefined;
  const projectId = searchParams.get('projectId') ?? undefined;
  const employeeId = searchParams.get('employeeId') ?? undefined;
  const groupByParam = searchParams.get('groupBy');
  const groupBy: ActivityLogGroupBy | undefined =
    groupByParam === 'client' || groupByParam === 'project' ? groupByParam : undefined;
  const groupKey = searchParams.get('groupKey') ?? undefined;

  const { data, isLoading } = useActivityLogEvents({
    periodQs: mockupMode ? null : periodQs,
    app,
    title,
    clientId,
    projectId,
    employeeId,
    groupBy,
    groupKey,
  });

  // Stabilise `events` so memoised derivations (column array,
  // screenshotEntries) below don't re-run on every render — `data?.events
  // ?? []` would otherwise produce a fresh array each pass.
  const events = useMemo(() => data?.events ?? [], [data?.events]);
  const tz = data?.org.timezone ?? 'UTC';

  // Flatten the rows that have a screenshot into the navigation list
  // the lightbox cycles through. The page owns this (and the
  // wrap-around math in handlePrev/handleNext) so the lightbox stays a
  // controlled component.
  const screenshotEntries = useMemo<ScreenshotLightboxEntry[]>(
    () =>
      events
        .filter((e): e is ActivityLogEvent & { screenshotId: string } => e.screenshotId !== null)
        .map((e) => ({
          screenshotId: e.screenshotId,
          occurredAt: e.occurredAt,
          employeeName: e.employee?.name ?? null,
          altText: altTextForEvent(e),
        })),
    [events],
  );

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const handlePrev = useCallback(() => {
    setLightboxIndex((i) =>
      i === null || screenshotEntries.length === 0
        ? i
        : (i - 1 + screenshotEntries.length) % screenshotEntries.length,
    );
  }, [screenshotEntries.length]);

  const handleNext = useCallback(() => {
    setLightboxIndex((i) =>
      i === null || screenshotEntries.length === 0 ? i : (i + 1) % screenshotEntries.length,
    );
  }, [screenshotEntries.length]);

  // Build the back-link path: same `/reports/activity-log` plus the
  // filter set the user came in with. Drop the row-identity params
  // (`app`, `title`, `groupKey`) since those don't apply on the list.
  const backHref = useMemo(() => {
    const next = new URLSearchParams();
    if (clientId) next.set('clientId', clientId);
    if (projectId) next.set('projectId', projectId);
    if (employeeId) next.set('employeeId', employeeId);
    if (groupBy) next.set('groupBy', groupBy);
    const qs = next.toString();
    return qs ? `/reports/activity-log?${qs}` : '/reports/activity-log';
  }, [clientId, projectId, employeeId, groupBy]);

  const titleHeader = title ? `${app} — ${title}` : app || '(unknown activity)';
  const subtitleParts: string[] = [];
  if (events.length > 0 && data) {
    subtitleParts.push(
      `${events.length} event${events.length === 1 ? '' : 's'}` +
        (data.totalEvents > events.length
          ? ` (showing ${events.length} of ${data.totalEvents})`
          : ''),
    );
  }
  if (groupBy && groupKey) {
    // Use the joined client/project label off the first event since the
    // backend doesn't echo it; every event in this list shares the same
    // group.
    const first = events[0];
    if (first) {
      const groupLabel = groupBy === 'client' ? first.project.clientName : first.project.name;
      subtitleParts.push(`${groupBy === 'client' ? 'Client' : 'Project'}: ${groupLabel}`);
    }
  }
  const subtitle = subtitleParts.join(' · ');

  const columns = useMemo(
    () => [
      {
        key: 'thumbnail' as const,
        header: 'Screenshot',
        // Fixed-width column so the rest of the table doesn't reflow when
        // an image arrives. Width matches the `width=120` request to
        // `<ScreenshotImage>` so the wrapper reserves the same box. Width
        // is read from `--thumbnail-cell-width` per DESIGN-RULES Rule 24
        // (no hardcoded pixel values in components).
        width: 'var(--thumbnail-cell-width)',
        render: (row: ActivityLogEvent) => {
          if (row.screenshotId === null) {
            return <div className={styles.thumbnailCell} />;
          }
          const screenshotId = row.screenshotId;
          return (
            <div className={styles.thumbnailCell}>
              <ScreenshotImage
                screenshotId={screenshotId}
                width={120}
                alt={altTextForEvent(row)}
                clickAriaLabel={`Open ${altTextForEvent(row)} at full size`}
                onClick={() => {
                  const idx = screenshotEntries.findIndex((e) => e.screenshotId === screenshotId);
                  if (idx >= 0) setLightboxIndex(idx);
                }}
              />
            </div>
          );
        },
      },
      {
        key: 'timestamp' as const,
        header: 'Timestamp (local)',
        render: (row: ActivityLogEvent) => (
          <span className={styles.mono}>{formatDateTimeInTz(row.occurredAt, tz)}</span>
        ),
      },
      {
        key: 'employee' as const,
        header: 'Employee',
        render: (row: ActivityLogEvent) => row.employee?.name ?? '—',
      },
      {
        key: 'event' as const,
        header: 'Event',
        render: (row: ActivityLogEvent) => row.site ?? row.url ?? '—',
      },
    ],
    [tz, screenshotEntries],
  );

  return (
    <>
      <PageHeader title={titleHeader} subtitle={subtitle || undefined} />
      <div className={styles.backRow}>
        <Link to={backHref} className={styles.backLink}>
          ← Back to Activity Log
        </Link>
      </div>
      <DataTable<ActivityLogEvent, ColumnKey>
        caption={`Events for ${titleHeader}, newest first`}
        columns={columns}
        rows={events}
        getRowKey={(row) => row.id}
        emptyState={isLoading ? 'Loading…' : 'No events match this row.'}
      />
      <ScreenshotLightbox
        open={lightboxIndex !== null}
        entries={screenshotEntries}
        index={lightboxIndex ?? 0}
        tz={tz}
        onClose={() => setLightboxIndex(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </>
  );
}
